import debugService from './debugService';
import { loginUrlWithReturn } from '../utils/authRedirect';

const resolveApiBaseUrl = () => {
  const baseUrl =
    process.env.REACT_APP_API_URL ||
    '/api';

  if (
    typeof window !== 'undefined' &&
    window.location?.protocol === 'https:' &&
    typeof baseUrl === 'string' &&
    baseUrl.startsWith('http://')
  ) {
    return '/api';
  }

  return baseUrl;
};

const API_BASE_URL = resolveApiBaseUrl();

// URL de base du serveur pour les images/fichiers statiques
// En local HTTP : passer par le proxy (/storage) pour rester same-origin
const STORAGE_ORIGIN = process.env.REACT_APP_STORAGE_URL || 'https://dev.api.chinexpress.com';

const STORAGE_BASE_URL =
  typeof window !== 'undefined' && window.location?.hostname === 'localhost'
    ? ''
    : STORAGE_ORIGIN;

// Origines déjà servies par ce même serveur. La base de données garde des URL
// absolues écrites avant le nom de domaine : elles pointent au bon endroit mais
// en clair, et un navigateur en HTTPS les bloquerait.
const KNOWN_STORAGE_ORIGINS = [STORAGE_ORIGIN, 'http://168.231.85.51:8081'];

const toRelativeStoragePath = (url) => {
  const origin = KNOWN_STORAGE_ORIGINS.find((o) => url.startsWith(o));
  return origin ? url.slice(origin.length) : null;
};

/**
 * Vérifie si on est en contexte HTTPS (pour éviter mixed content)
 */
const isHttpsContext = () => {
  return typeof window !== 'undefined' && window.location?.protocol === 'https:';
};

/**
 * Construit l'URL complète d'une image à partir du chemin retourné par l'API
 * @param {string} path - Le chemin de l'image (peut être relatif ou absolu)
 * @returns {string|null} L'URL complète de l'image ou null si pas de chemin
 */
export const getImageUrl = (path) => {
  if (!path) return null;

  // Si c'est déjà une URL complète
  if (path.startsWith('http://') || path.startsWith('https://')) {
    const relative = toRelativeStoragePath(path);

    // En HTTPS comme en local, repasser par le proxy /storage : same-origin,
    // pas de mixed content, et les vieilles URL en clair redeviennent valides.
    if (
      relative &&
      (isHttpsContext() ||
        (typeof window !== 'undefined' && window.location?.hostname === 'localhost'))
    ) {
      return relative;
    }

    return relative ? `${STORAGE_ORIGIN}${relative}` : path;
  }

  // En HTTPS ou localhost, chemins relatifs (passent par le proxy)
  // Sinon URL complète vers le serveur storage
  const baseUrl =
    isHttpsContext() ||
    (typeof window !== 'undefined' && window.location?.hostname === 'localhost')
      ? ''
      : STORAGE_BASE_URL;

  // Si le chemin commence par /, l'ajouter à la base URL
  if (path.startsWith('/')) {
    return `${baseUrl}${path}`;
  }

  // Sinon, ajouter /storage/ devant (convention Laravel)
  return `${baseUrl}/storage/${path}`;
};

/**
 * Cache mémoire des lectures.
 *
 * L'API vit sur un serveur distant : un aller-retour à vide coûte déjà ~500 ms.
 * Sans cache, revenir sur une page déjà vue relance tout et réaffiche un
 * squelette alors que la donnée n'a pas bougé. On garde donc la réponse d'un
 * GET pendant `GET_CACHE_TTL`, et on partage la promesse en vol pour que deux
 * composants montés ensemble ne fassent qu'une requête.
 *
 * Toute écriture (POST/PUT/PATCH/DELETE) vide le cache : l'écran suivant
 * repart de la donnée du serveur.
 */
const GET_CACHE_TTL = 30_000;

const getCache = new Map();
const inFlight = new Map();

export const clearApiCache = () => {
  getCache.clear();
  inFlight.clear();
};

/**
 * Classe de service API pour gérer les appels HTTP
 */
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Récupère le token d'authentification depuis le localStorage
   */
  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  /**
   * Définit le token d'authentification dans le localStorage
   */
  setAuthToken(token) {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  /**
   * Effectue un appel API générique
   * @param {string} endpoint
   * @param {RequestInit & { _retried429?: boolean }} options
   */
  async request(endpoint, options = {}) {
    const { _retried429 = false, ...fetchOptions } = options;
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...fetchOptions.headers,
      },
      ...fetchOptions,
    };

    // Ajouter le token si disponible
    const token = this.getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Debug: log de la requête
    const method = fetchOptions.method || 'GET';
    const requestId = debugService.logRequest(method, url, fetchOptions.body, config.headers);
    const startTime = performance.now();

    try {
      const response = await fetch(url, config);
      const duration = Math.round(performance.now() - startTime);

      // Debug: log de la réponse
      debugService.logResponse(requestId, {
        status: response.status,
        statusText: response.statusText,
      }, duration);

      // Un seul retry si Retry-After raisonnable (< 10s)
      if (response.status === 429 && !_retried429) {
        const retryAfterRaw = response.headers.get('Retry-After');
        const retryAfterSec = retryAfterRaw ? parseFloat(retryAfterRaw) : NaN;
        if (Number.isFinite(retryAfterSec) && retryAfterSec > 0 && retryAfterSec < 10) {
          await new Promise((resolve) => setTimeout(resolve, retryAfterSec * 1000));
          return this.request(endpoint, { ...fetchOptions, _retried429: true });
        }
      }

      return await this.handleResponse(response);
    } catch (error) {
      // Debug: log de l'erreur
      debugService.logError(error, `${method} ${endpoint}`);
      if (error.status) throw error;
      throw new Error(`Erreur réseau: ${error.message}`);
    }
  }

  /**
   * Gère la réponse de l'API
   */
  async handleResponse(response) {
    // Gérer les réponses 204 No Content
    if (response.status === 204) {
      return null;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      console.error('Erreur API:', {
        status: response.status,
        url: response.url,
        errorData
      });
      
      // Erreur de validation (422)
      if (response.status === 422 && errorData.errors) {
        const details = Object.values(errorData.errors).flat().filter(Boolean);
        const error = new Error(
          details.length ? details.join(' ') : (errorData.message || 'Erreur de validation')
        );
        error.validationErrors = errorData.errors;
        error.status = 422;
        error.response = { data: errorData };
        throw error;
      }
      
      // Authentification (401)
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.location.href = loginUrlWithReturn();
      }

      // Rate-limit Laravel (429)
      if (response.status === 429) {
        const error = new Error(
          errorData.message && !/too many attempts/i.test(errorData.message)
            ? errorData.message
            : 'Trop de requêtes, réessaie dans un instant.'
        );
        error.status = 429;
        error.response = { data: errorData };
        throw error;
      }
      
      // Erreurs serveur (500)
      if (response.status === 500) {
        const error = new Error(errorData.message || 'Erreur serveur interne');
        error.status = 500;
        error.response = { data: errorData };
        error.details = errorData;
        throw error;
      }
      
      const error = new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      error.status = response.status;
      error.response = { data: errorData };
      throw error;
    }
    
    return response.json();
  }

  /**
   * Méthodes HTTP
   */
  /**
   * @param {object} options - `{ fresh: true }` ignore le cache (après une écriture
   *   faite ailleurs, ou pour un rafraîchissement demandé par l'utilisateur).
   */
  async get(endpoint, params = {}, options = {}) {
    const query = new URLSearchParams(params).toString();
    const url = query ? `${endpoint}?${query}` : endpoint;

    if (options.fresh) {
      getCache.delete(url);
    } else {
      const cached = getCache.get(url);
      if (cached && Date.now() - cached.at < GET_CACHE_TTL) {
        return cached.data;
      }
      const pending = inFlight.get(url);
      if (pending) return pending;
    }

    const promise = this.request(url)
      .then((data) => {
        getCache.set(url, { at: Date.now(), data });
        return data;
      })
      .finally(() => {
        inFlight.delete(url);
      });

    inFlight.set(url, promise);

    return promise;
  }

  async post(endpoint, data = {}) {
    clearApiCache();

    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data = {}) {
    clearApiCache();

    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT avec FormData (pour les fichiers)
   */
  async putFormData(endpoint, formData) {
    clearApiCache();

    return this.request(endpoint, {
      method: 'PUT',
      body: formData,
      headers: {}, // Ne pas définir Content-Type pour FormData
    });
  }

  async patch(endpoint, data = {}) {
    clearApiCache();

    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    clearApiCache();

    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * Upload de fichiers (multipart/form-data)
   */
  async upload(endpoint, formData) {
    clearApiCache();

    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      headers: {}, // Ne pas définir Content-Type pour laisser le navigateur le faire
    });
  }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
